/* eslint-disable no-inner-declarations */

import { breakpoint } from "@porsche-design-system/components-js/styles";
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import {
  RoutingKeyPointers,
  RoutingKeyWithClick,
} from "@phn/entities/routing-key";
import { routerStore } from "@phn/state/router-store";
import {
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType,
} from "@phn/utility/constants";
import {
  getHTMLElementFromRoot,
  isDesktop,
  IsKeyboardClick,
} from "@phn/utility/helper";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import * as routing from "@phn/services/routing-manager";
import { navStateStore } from "@phn/state/nav-state-store";
import { Application, ApplicationType } from "@phn/utility/constants/app";
import {
  closeNavigationDrawer,
  openNavigationDrawer,
} from "@phn/services/navigation-drawer-service";
import { navContentStore } from "@phn/state/nav-content-store";
import { drawerTimeout } from "@phn/utility/constants/timeouts";
import { getLogoAriaLabel } from "@phn/utility/logo";
import { constructThemeClass } from "../../utility/helper";

@Component({
  tag: "phn-level-0",
  styleUrl: "level-0.scss",
  scoped: true,
})
export class Level0 {
  @Prop() locale!: string;

  @Prop() breakpoint!: number;

  @Prop() loadingContent = false;

  @Prop() receivedContent = false;

  @Prop() theme: NavigationThemeType = NavigationTheme.light;

  @Prop() mode: NavigationModeType = NavigationMode.navbar;

  @Prop() app: ApplicationType = Application.default;

  @Prop() displayLogoOnly = false;

  @Prop() limitedWidth = false;

  @Prop() hideShowOnScroll?: boolean;

  @Prop() initialShowCountryRecommender?: boolean;

  @Prop() showCountryRecommender?: boolean;

  @State() usingKeyboard = false;

  @State() scrolledPastHeader = false;

  @State() isNavigationLoaded = false;

  @State() isLoading = false;

  @State() isAccountHoveredOn = false;

  @State() eventController = new AbortController();

  @State() recommenderHeight: number | undefined;

  @Element() el!: HTMLPhnLevel0Element;

  @Event() navigationLoaded!: EventEmitter<boolean>;

  private drawerTimeout = new Timeout(drawerTimeout);

  @Listen("resize", { target: "window", passive: true })
  getCountryHeight(): void {
    this.setCountryHeightCorrection(false);
  }

  @Watch("hideShowOnScroll")
  hideShowOnScrollWatcher(val: boolean): void {
    this.connectedCallback();
    if (!val) {
      this.eventController.abort();
    }
  }

  @Watch("loadingContent")
  loadingContentWatcher(): void {
    this.setNavigationLoaded();
  }

  /* istanbul ignore next */
  @Watch("breakpoint")
  breakpointWatcher(): void {
    this.updateRoutingKeyPointerOfMobileLevel1();
    this.setNavigationLoaded();
  }

  @Watch("receivedContent")
  receivedContentWatcher(): void {
    this.setNavigationLoaded();
  }

  @Watch("showCountryRecommender")
  showCountryRecommenderWatcher(value: boolean): void {
    if (value) {
      this.setCountryHeightCorrection();
    } else {
      this.el.style.removeProperty("--country-correction");
      this.el.style.removeProperty("--country-height");
    }
  }

  private setCountryHeightCorrection(withAnimations = true): void {
    const elem = getHTMLElementFromRoot("phn-country-recommender");

    if (elem) {
      (withAnimations
        ? Promise.all(
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            elem.getAnimations().map((animation) => animation.finished),
          )
        : Promise.resolve()
      ).then(() => {
        this.recommenderHeight = elem.offsetHeight;
        this.el.style.setProperty(
          "--country-height",
          `${this.recommenderHeight}px`,
        );
      });
    } else {
      setTimeout(() => this.setCountryHeightCorrection(), 50);
    }
  }

  @Listen("scroll", { target: "window", passive: true })
  scrollHandler(): void {
    this.scrolledPastHeader = this.el.getBoundingClientRect().bottom <= 0;
  }

  @Listen("keydown", { target: "window", passive: true })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      this.usingKeyboard = true;
      navStateStore.state.usingKeyboard = true;

      closeNavigationDrawer();

      if (!routing.isClosed()) {
        routing.reset();
      }
    }
  }

  @Listen("click", { passive: true })
  handleClick(e: MouseEvent): void {
    this.usingKeyboard = IsKeyboardClick(e);
    navStateStore.state.usingKeyboard = IsKeyboardClick(e);
  }

  @Listen("openDrawer")
  async handleOpenDrawer(
    event: CustomEvent<RoutingKeyWithClick>,
  ): Promise<void> {
    await this.openDrawer(event.detail);
  }

  componentWillLoad(): void {
    this.setNavigationLoaded();
  }

  /* istanbul ignore next */
  private updateRoutingKeyPointerOfMobileLevel1(): void {
    if (
      routerStore.state.routingKeyPointer ===
      RoutingKeyPointers.LEVEL_ONE.valueOf()
    ) {
      routerStore.state.routingKeyPointer = isDesktop(this.breakpoint)
        ? RoutingKeyPointers.LEVEL_TWO
        : RoutingKeyPointers.LEVEL_ONE;
    }
  }

  private setNavigationLoaded(): void {
    this.isNavigationLoaded = !this.loadingContent;
    this.navigationLoaded.emit(this.isNavigationLoaded);
  }

  private async openDrawer(
    routingKeyWithClick: RoutingKeyWithClick,
  ): Promise<void> {
    const { initialRoutingKey, isKeyboardClick } = routingKeyWithClick;

    this.isLoading = true;
    this.usingKeyboard = isKeyboardClick;
    navStateStore.state.usingKeyboard = isKeyboardClick;

    const backdropComponent = "phn-nd-backdrop";

    const routingKey =
      initialRoutingKey === ""
        ? navStateStore.state.initialRoutingPath
        : initialRoutingKey;
    this.openMenuDrawer(routingKey ?? "");
    try {
      await componentsOnReady(
        [this.el, ".drawer", backdropComponent],
        this.drawerTimeout,
      );
    } finally {
      this.isLoading = false;
    }
  }

  private openMenuDrawer(initialRoutingKeys: string): void {
    openNavigationDrawer(initialRoutingKeys);

    routing.goTo(...routing.mountRouteFromString(initialRoutingKeys));
  }

  connectedCallback(): void {
    if (!this.hideShowOnScroll) {
      return;
    }

    setTimeout(() => {
      try {
        let lastScroll = window.scrollY;
        let ticking = false;
        const updateScrollFn = updateScroll.bind(this);

        if (this.initialShowCountryRecommender) {
          if (lastScroll) this.el.classList.add("initial-country-load");
          this.setCountryHeightCorrection();
        }

        window.addEventListener("scroll", onScroll, {
          passive: true,
          signal: this.eventController.signal,
        });

        // eslint-disable-next-line func-style
        function onScroll(): void {
          if (!ticking) {
            requestAnimationFrame(updateScrollFn);
            ticking = true;
          }
        }

        // eslint-disable-next-line func-style
        function updateScroll(this: Level0): void {
          const currentScroll = window.scrollY;

          if (this.isLoading) {
            // Do not update scroll when drawer is opening
            this.el.classList.add("navi-opened");
            ticking = false;
            return;
          }
          this.el.classList.remove("navi-opened");

          if (Math.abs(currentScroll - lastScroll) > 3) {
            if (currentScroll > lastScroll) {
              this.el.classList.add("hide");
              this.el.dataset.pinned = "false";
              this.el.dispatchEvent(
                new CustomEvent("headerPinned", {
                  composed: true,
                  detail: false,
                }),
              );

              if (this.el.classList.contains("initial-country-load")) {
                this.el.addEventListener(
                  "transitionend",
                  () => this.el.classList.remove("initial-country-load"),
                  { once: true },
                );
              }
            } else {
              this.el.classList.remove("hide");
              this.el.dataset.pinned = "true";
              this.el.dispatchEvent(
                new CustomEvent("headerPinned", {
                  composed: true,
                  detail: true,
                }),
              );
            }
          }

          if (this.showCountryRecommender) {
            this.el.style.setProperty(
              "--country-correction",
              `-${Math.min(this.recommenderHeight ?? 0, currentScroll)}px`,
            );
          }

          lastScroll = currentScroll;
          ticking = false;
        }
        /* eslint-disable-next-line no-empty */
      } catch {}
    }, 1000);
  }

  disconnectedCallback(): void {
    this.drawerTimeout.cancel();
    this.eventController.abort();
  }

  private getLogoSize(): string {
    return this.breakpoint >= breakpoint.m ? "medium" : "small";
  }

  private getLogo(): HTMLElement {
    const { shop, level0, crest } = navContentStore.state;

    return (
      <div class="level-0-content-logo tooltip">
        <phn-logo
          app={this.app}
          theme={this.theme}
          mode={this.mode}
          size={this.getLogoSize()}
          breakpoint={this.breakpoint}
        />
        {crest && isDesktop(this.breakpoint) ? (
          <span class="tooltip-text logo">
            {getLogoAriaLabel(this.locale, this.app, shop, level0)}
          </span>
        ) : null}
      </div>
    );
  }

  render(): HTMLPhnLevel0Element {
    return (
      <Host
        class={`${constructThemeClass(
          this.mode,
          this.theme,
        )}-theme themed-background ${this.hideShowOnScroll ? "hide-show-on-scroll" : "default"}`}
      >
        <header class={this.limitedWidth ? "limited-width" : ""}>
          {routing.isOpened() || routing.isOpening() ? (
            <phn-backdrop style={this.isLoading ? { display: "none" } : {}} />
          ) : null}

          <div class="level-0-content">
            {!this.displayLogoOnly ? (
              <div class="level-0-content-icons">
                <phn-level-0-icons
                  breakpoint={this.breakpoint}
                  isLoading={this.isLoading}
                  isNavigationLoaded={this.isNavigationLoaded}
                >
                  {this.getLogo()}
                </phn-level-0-icons>
              </div>
            ) : (
              this.getLogo()
            )}
          </div>
        </header>
      </Host>
    ) as HTMLPhnLevel0Element;
  }
}
